<template>
  <div
  class="feedback"
  :class="{'feedback--visible' : isVisible}"
  >
    <div class="feedback__overlay"></div>

    <div class="feedback__main">
      <div class="feedback__content">
        <LoadingBox v-if="isLoading" />
        
        <CloseButton @click="close" />

        <header v-if="!isFeedbackSent" class="feedback__header">
          <h3 class="feedback__header__title">Queremos sua opinião!</h3>
          <div class="feedback__header__subtitle">Ninguém melhor para guiar o nosso caminho do que você! Conta pra gente, qual sua sugestão para nossa plataforma?</div>
        </header>

        <div class="feedback__content__inner">
          <div v-if="!isFeedbackSent">
            <div v-if="isRateLimited" class="mb-3 alert border-danger text-danger">
              {{ errorMessage }}
            </div>
            
            <transition
              enter-active-class="animate__animated animate__fadeIn"
              leave-active-class="animate__animated animate__fadeOut"
              :duration="{ leave: 250 }"
              mode="out-in"
            >
              <FeedbackItemList
                v-if="!group"
                ref="itemList"
                :items="feedbackDataset"
                @select="selectGroup"
              >
                <template v-slot="{ item }">
                  {{ item.name }}
                </template>
              </FeedbackItemList>

              <FeedbackSelector
                v-else-if="group.type !== 'help'"
                :group="group"
                @submit="submitFeedback"
                @back="backToGroups"
              />
            </transition>
          </div>

          <FeedbackThanks
            v-if="isFeedbackSent"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { storeFeedback } from '@/api/feedback.api'
  import FeedbackItemList from '@/components/feedback/FeedbackItemList'
  import FeedbackSelector from '@/components/feedback/FeedbackSelector'
  import FeedbackThanks from '@/components/feedback/FeedbackThanks'
  import CloseButton from '@/components/buttons/CloseButton'
  import LoadingBox from '@/components/layout/LoadingBox'
  import 'animate.css/animate.min.css'

  export default {
    components: {
      FeedbackItemList,
      FeedbackSelector,
      CloseButton,
      FeedbackThanks,
      LoadingBox,
    },

    data: () => ({
      feedbackDataset: [
        { name: 'Sugerir criação de curso' },
        { name: 'Sugerir criação de e-book' },
        { name: 'Sugerir tema para criação de conteúdo' },
        { name: 'Sugerir funcionalidade para a plataforma' },
        { name: 'Sugerir melhoria de uma funcionalidade' },
        { name: 'Relatar problema na plataforma' },
        { name: 'Sugerir outro assunto' },
        { name: 'Tenho uma dúvida/preciso de ajuda', type: 'help' }
      ],
      isVisible: false,
      group: null,
      isLoading: false,
      isFeedbackSent: false,
      isRateLimited: false,
      errorMessage: ''
    }),

    watch: {
      isVisible (newState) {
        if (!newState) {
          this.isLoading = this.isFeedbackSent = this.isRateLimited = false
        }
      }
    },
  
    methods: {
      async submitFeedback (feedback) {
        this.triggerTrackEvent(feedback)

        try {
          this.isLoading = true

          await this.storeFeedback(feedback)

          this.isLoading = false

          this.isFeedbackSent = true
        } catch (err) {
          if (err.status === 429) {
            this.isLoading = false
            this.errorMessage = err.data.message
            return this.isRateLimited = true
          }
          throw err
        }
      },

      async storeFeedback (feedback) {
        await storeFeedback(feedback)
      },

      triggerTrackEvent (feedback) {
        this.$gtm.trackEvent({
          event: 'feedback',
          ...feedback
        })
      },

      selectGroup (group) {
        if (group.type === 'help') {
          this.close()
          this.$router.push({ name: 'support' })
        } else {
          this.group = group
        }
      },

      backToGroups () {
        this.group = null
      },

      open () {
        this.isVisible = true
      },

      close () {
        this.isVisible = false
        this.isFeedbackSent = false
        this.group = null
        this.$refs.itemList.reset()
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "~@/assets/sass/variables";

  .feedback {
    $p: &;

    z-index: 1000;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 15px;
    transition: .5s opacity;
    transform: translateY(-100%);
    opacity: 0;

    &__header {
      margin-bottom: 24px;
    }

    &__overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(#000, .6);
      z-index: 1;
    }

    &__main {
      background-color: $purple900;
    }

    &__content {
      z-index: 2;
      position: relative;
      display: flex;
      flex-direction: column;
      max-width: 700px;
      box-shadow: 0px 8px 12px 2px $purple900;
      border-radius: 8px;
      padding: 30px;
      opacity: 0;
      transition: .5s all;
      transition-delay: .25s;
      max-height: calc(100vh - 60px);
      background-color: $yellowBorder;
      
      &::before {
        content: "";
        position: absolute;
        inset: 0;
        padding: 1px;
        border-radius: inherit;
        background: $borderGradient;
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box, 
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        z-index: -1;
      }

      &__inner {
        height: 100%;
        overflow: auto;
      }
    }

    ::v-deep &__header {

      &__title {

      }

      &__subtitle {

      }
    }

    &--visible {
      transform: translateY(0);
      opacity: 1;

      #{$p}__content { 
        opacity: 1;
      }
    }

    @media (max-width: 992px) {
      &__content { 
        padding: 15px; 
      }
      &__header__title { 
        font-size: 24px;
      }
      &__header__subtitle { 
        font-size: 15px;
      }
    }
  }
</style>